package fyi.moko.hotpl.domain.model

/**
 * One shipped build, described for the people testing it.
 *
 * 알파 기간에는 앱을 보는 사람이 전부 리뷰어다. 무엇이 바뀌었는지 앱 밖(메신저, PR)에서
 * 따로 알려 주면 화면과 설명이 어긋나기 시작하므로, 빌드에 같이 실려 다니게 둔다.
 */
data class ChangelogEntry(
    /** Stable identifier for "has the reader seen this one" — never reuse or reorder. */
    val version: String,
    val date: String,
    val headline: String,
    /**
     * What changed, in the reader's terms rather than the code's: each line should be
     * something a reviewer can go and try.
     */
    val changes: List<String>,
)

/** The shipped changelog, newest first. */
object Changelog {

    val entries: List<ChangelogEntry> = listOf(
        ChangelogEntry(
            version = "2026-10-04",
            date = "Oct 4, 2026",
            headline = "A calmer map that looks like the app",
            changes = listOf(
                "The map now wears the journal's warm colours, and the restaurant, shop " +
                    "and transit icons are gone, so your own pins are what you see. Street " +
                    "and neighbourhood names stay.",
                "iPhone and web now use the same map as Android — no more mix of " +
                    "Apple Maps and OpenStreetMap.",
                "Been and Want to go chips show the pin they filter for: a solid red pin " +
                    "for places you've been, a hollow one for places you haven't.",
                "Android: when pins bunch up, the number bubble is now the app's warm " +
                    "orange instead of Google blue.",
            ),
        ),
        ChangelogEntry(
            version = "2026-09-20b",
            date = "Sep 28, 2026",
            headline = "Friends' changes show up as they happen",
            changes = listOf(
                "When a friend shares a place, unshares it, or logs a visit, your Friends " +
                    "tab, the \"You both want to go\" cards and the badge on your Journal card " +
                    "update on their own — no more reopening a friend or leaving the screen " +
                    "to see it.",
                "Friends' photos load once and stay for the session, so the Friends list " +
                    "and previews stop re-downloading the same pictures.",
                "Friend badges on Journal cards show just their name, and stay readable on " +
                    "the selected (orange) card — so do \"Revisit soon\" badges.",
                "Choosing \"Want to go\" no longer drops a suggested pin where you're standing " +
                    "(the form stopped saying \"You're here right now\" for a place you haven't been).",
                "iPhone: Open in Maps and Directory rows open Google Maps when it's " +
                    "installed, Apple Maps otherwise.",
            ),
        ),
        ChangelogEntry(
            version = "2026-09-20",
            date = "Sep 20, 2026",
            headline = "Friends now see what you ate",
            changes = listOf(
                "A shared place now carries up to three food photos from your visits, newest " +
                    "first, next to the cover photo. Log a visit with food photos and friends " +
                    "see them without you doing anything else.",
                "Friend's place preview: tap the small photos under the big one to look at each.",
                "Friends tab no longer said you were logged out right after opening the web " +
                    "app — fixed.",
                "Hearts in the Friends list now draw on the web too.",
            ),
        ),
        ChangelogEntry(
            version = "2026-09-19d",
            date = "Sep 19, 2026",
            headline = "Your journal now says who else wants to go",
            changes = listOf(
                "Journal: a Want-to-go card gets a small \"Alice wants to go too\" line when a " +
                    "friend shares that place as Want to go. Same line on the place's own " +
                    "screen, under the Private / Friends / Public chips.",
                "It reads your friends' lists when Journal opens, so it can take a moment " +
                    "to appear and it's gone while offline. Logging a visit removes it — " +
                    "you've been now.",
                "Friends tab: the \"You both want to go\" cards open the friend's view of " +
                    "the place; it says \"In your journal\" instead of offering to add it again.",
            ),
        ),
        ChangelogEntry(
            version = "2026-09-19c",
            date = "Sep 19, 2026",
            headline = "Places you both want to go to",
            changes = listOf(
                "Friends tab, top of the list: \"You both want to go\" — every place a friend " +
                    "shares as Want to go that you also have as Want to go. Cafe Concerto " +
                    "should be there for the two of you.",
                "The share button on each card sends the name and map link to Messages or " +
                    "KakaoTalk. Making the plan still happens there, not in the app.",
                "Only what a friend has shared with Friends can match, and a place saved " +
                    "under a different name won't — say if one you expected is missing.",
            ),
        ),
        ChangelogEntry(
            version = "2026-09-19b",
            date = "Sep 19, 2026",
            headline = "The eight feelings, in restaurant words",
            changes = listOf(
                "Lively → Buzzing, At ease → Laid-back, Serene → Quiet, Restored → " +
                    "Recharged, Awe → Blown away, Wistful → Nostalgic. Adventurous and " +
                    "Cozy stay. Same colours, same order, same eight — only the words.",
                "Anything you renamed yourself in Account → Feeling names is kept as is.",
                "Still eight, on purpose: I want to see whether the words alone fix it " +
                    "before hiding any. One question — is there a word here you don't get?",
            ),
        ),
        ChangelogEntry(
            version = "2026-09-19",
            date = "Sep 19, 2026",
            headline = "The app now notes which buttons you use",
            changes = listOf(
                "To make the app simpler, I need to know what actually gets used — which " +
                    "chips, which menu items, which screens. From this build the app sends " +
                    "me that: taps and screen names only. Never your notes, photos, place " +
                    "names or tags.",
                "Account → \"Share how you use the app\" turns it off. It's on by default " +
                    "for the two of us.",
                "Nothing else changed in this build.",
            ),
        ),
        ChangelogEntry(
            version = "2026-09-18",
            date = "Sep 18, 2026",
            headline = "Friends now see the place, not just its name",
            changes = listOf(
                "Sharing a place with Friends now sends its cover photo plus the phone number " +
                    "and website. Nothing else changed: notes, feelings and people photos stay " +
                    "private. The line under the Friends chip says exactly what goes out.",
                "Friends tab: tap a shared place to look at it first — photo, tags, phone, " +
                    "website, map — then \"Add to Want to go\". The old + that saved " +
                    "straight away is gone.",
                "Adding a friend's place saves it as somewhere you want to go, without their " +
                    "visited status or heart. Their photos stay theirs.",
                "Not yet: food photos from visits. That's next.",
            ),
        ),
        ChangelogEntry(
            version = "2026-09-13b",
            date = "Sep 13, 2026",
            headline = "Call each feeling whatever fits you",
            changes = listOf(
                "Account → Feeling names: rename any of the eight feelings (say, Cozy → " +
                    "Snug). The colour and the order stay the same; only the word changes, " +
                    "and it shows up in Log a Visit, the visit timeline and the " +
                    "\"What do you need right now?\" chips.",
                "Names live on this device only — they don't sync, and nobody else sees them.",
            ),
        ),
        ChangelogEntry(
            version = "2026-09-13",
            date = "Sep 13, 2026",
            headline = "Places you haven't been yet show as hollow pins",
            changes = listOf(
                "On the map, a place you still want to go to is drawn as a hollow pin — " +
                    "white inside with a coloured edge — while places you've been keep " +
                    "their solid colour. Same rule as the Been / Want to go chips, so " +
                    "the two always agree.",
            ),
        ),
        ChangelogEntry(
            version = "2026-09-12",
            date = "Sep 12, 2026",
            headline = "The icon you asked for: a pin with a spoon and fork making a heart",
            changes = listOf(
                "The app icon is now your latest design — the pin is back, and inside it the " +
                    "spoon and fork are drawn out so they read as cutlery, meeting at the " +
                    "heart's point. Only the icon changed in this build.",
            ),
        ),
        ChangelogEntry(
            version = "2026-09-11c",
            date = "Sep 11, 2026",
            headline = "A new icon to try on: a heart over crossed spoon and fork",
            changes = listOf(
                "The app icon is the heart with a spoon and fork crossed beneath it — the " +
                    "design you sent. Nothing else changed in this build, so you can compare " +
                    "it with the pin icon from the last one.",
            ),
        ),
        ChangelogEntry(
            version = "2026-09-11b",
            date = "Sep 11, 2026",
            headline = "Been here, or want to go? Your journal can tell them apart now",
            changes = listOf(
                "Two new chips above your places: Been and Want to go. Tap one to see only " +
                    "that side of your journal — for the days you want somewhere new, and the " +
                    "days you want the usual. Tap again to see everything.",
                "A place counts as Want to go when you saved it as one, or when it has no " +
                    "visit logged yet. Logging a visit moves it over.",
                "Backups now keep which places are Want to go, so a restore doesn't turn " +
                    "them all into places you've been.",
                "If a photo's file disappeared from this phone before it could be backed up, " +
                    "the app now says so — a warning mark instead of a spinner that never " +
                    "ends — so you know to take it again.",
            ),
        ),
        ChangelogEntry(
            version = "2026-09-11",
            date = "Sep 11, 2026",
            headline = "A new icon, pins you can see, and one tap from Directory to your journal",
            changes = listOf(
                "Hotpla has its own icon now — a pin with a heart of spoon and fork on coral. " +
                    "The green robot is gone.",
                "Map pins are back to the teardrop shape, big enough to spot at a glance. A " +
                    "place with no feeling yet is the familiar red; a place you have put a " +
                    "feeling to takes that colour. Lively is yellow now, so it never looks " +
                    "like a plain pin.",
                "Adding a place from Directory with + takes you straight to it, so you can " +
                    "put in a photo or a note while it is still on your mind.",
                "The little badge on a place — Just saved, Filling in, and so on — counts " +
                    "four kinds of detail: a visit, a photo, a tag, and a note or feeling. " +
                    "Tap it to see which ones this place has; tap an empty line to add it.",
            ),
        ),
        ChangelogEntry(
            version = "2026-09-10c",
            date = "Sep 10, 2026",
            headline = "Turn a photo the right way up, and read the map by colour",
            changes = listOf(
                "A photo that was saved lying on its side can be stood up: open it full " +
                    "screen and tap Rotate. It turns a quarter at a time, so press it again " +
                    "if once is not enough. The photo itself is rewritten, so it is straight " +
                    "everywhere afterwards — and on your other devices too.",
                "The journal opens the way you left it. Switch to the map and it will still " +
                    "be the map the next time you open the app; switch back to the list and " +
                    "it stays the list.",
                "Map pins now carry the colour of how a place felt — the same colour as the " +
                    "dot on its card. Places you have not put a feeling to stay grey, and " +
                    "the pin you have tapped is the one with the thick white ring.",
                "The journal can sort by name. Turn on A–Z in the filter bar, and once you " +
                    "have a few places an index appears down the right edge to jump you " +
                    "through the list.",
            ),
        ),
        ChangelogEntry(
            version = "2026-09-10b",
            date = "Sep 10, 2026",
            headline = "A tag filter can no longer hide your whole journal",
            changes = listOf(
                "Tapping a tag on a place card switches on a filter — easy to do by " +
                    "accident while reaching for the card, and the journal would go empty " +
                    "with nothing on screen to say why. Filters that are on now sit in the " +
                    "bar under the tabs, and one tap takes them off again.",
                "Adding a place asks whether you have been there. It answers for you when " +
                    "it can — from where a photo was taken, or from where you are standing " +
                    "— and a place saved from somewhere else starts as one to go to. " +
                    "Change it with a tap; it never changes it back.",
                "A photo you took earlier now dates the visit by the photo, not by the " +
                    "moment you added it.",
            ),
        ),
        ChangelogEntry(
            version = "2026-09-10",
            date = "Sep 10, 2026",
            headline = "Photos open full screen, whole and zoomable",
            changes = listOf(
                "Tap any photo — the header, the gallery, a visit's — and it opens full " +
                    "screen with the whole picture in view, nothing cropped away. Pinch to " +
                    "zoom, drag to move around, double-tap to go in and back out.",
                "Making a photo the cover, moving it to the place, or deleting it now lives " +
                    "in the viewer's ⋮ menu instead of a tap on the thumbnail.",
                "Logging a visit can take a photo with the camera, not only pick one from " +
                    "the library. A tap on one of those photos opens a menu now too, so it " +
                    "no longer disappears on the first tap.",
            ),
        ),
        ChangelogEntry(
            version = "2026-09-09d",
            date = "Sep 9, 2026",
            headline = "Tapping a photo while editing no longer deletes it",
            changes = listOf(
                "In Edit place, tapping a photo removed it there and then — the same tap " +
                    "on the place's page opens a menu. Now both open the menu, and deleting " +
                    "is the second, labelled tap. Photos already saved lying on their side " +
                    "still cannot be turned upright; that comes with the full-screen viewer.",
            ),
        ),
        ChangelogEntry(
            version = "2026-09-09c",
            date = "Sep 9, 2026",
            headline = "The keyboard no longer sits on top of the visit note",
            changes = listOf(
                "Logging a visit and typing a note meant typing blind — the keyboard covered " +
                    "the box you were writing in. The sheet now moves up out of its way, and " +
                    "scrolls if there is more than fits.",
            ),
        ),
        ChangelogEntry(
            version = "2026-09-09b",
            date = "Sep 9, 2026",
            headline = "Photos keep the way up you shot them",
            changes = listOf(
                "A photo taken holding the phone upright could come into the app lying on " +
                    "its side. Samsung cameras store the sensor's own sideways pixels and " +
                    "note the turn separately; the app was not reading that note. It does " +
                    "now, for pictures taken here and for ones shared from elsewhere. " +
                    "Photos already saved sideways stay that way for the moment.",
            ),
        ),
        ChangelogEntry(
            version = "2026-09-09",
            date = "Sep 9, 2026",
            headline = "Photos that never finished uploading get another try",
            changes = listOf(
                "A photo whose upload was interrupted could end up referenced by a place but " +
                    "stored nowhere, showing a retry glyph forever. Opening the app now sweeps " +
                    "once more for photos that are still on this phone but not in your account, " +
                    "and sends them. Leave the app open a minute or two after updating.",
            ),
        ),
        ChangelogEntry(
            version = "2026-09-08",
            date = "Sep 8, 2026",
            headline = "Journal search takes what you type",
            changes = listOf(
                "Journal search: the cursor no longer jumps mid-word, so what you type comes out " +
                    "in the order you typed it — in Korean and in English. If you still see letters " +
                    "land out of order, please say which phone and which keyboard app you use.",
            ),
        ),
        ChangelogEntry(
            version = "2026-09-06",
            date = "Sep 7, 2026",
            headline = "Your photos follow you, and a place page that grows",
            changes = listOf(
                "Photos now travel with your account. A photo taken on one phone shows up on your " +
                    "other devices, and the photos you had already saved were uploaded too.",
                "While a photo is still on its way, its tile shows a small spinner. If it never " +
                    "arrives — the phone it was taken on hasn't been online with this build yet — the " +
                    "tile shows a refresh mark you can tap to try again.",
                "A place's page fills in as you do: the header grows edge to edge as you add a " +
                    "visit, a photo, tags and a note, and a \"Regular spot\" badge appears after " +
                    "three visits. Swipe the header to flip through the place's photos.",
                "Journal search: typing Korean no longer scrambles the letters mid-word.",
            ),
        ),
        ChangelogEntry(
            version = "2026-08-30",
            date = "Aug 30, 2026",
            headline = "Closest first, and where the map opens",
            changes = listOf(
                "Searching Directory now leads with what is actually closest. Typing a chain name " +
                    "used to list branches kilometres away while the one down the road was missing.",
                "The map opens on where you are, instead of on the last place you saved — which " +
                    "could be an hour's drive away, with your own location off the screen.",
                "iPhone: the map has +/- buttons. Pinching used to be the only way to zoom.",
                "Search results come back in English on a Korean phone, and there are ten of them " +
                    "rather than twenty.",
                "This screen now tracks what you asked for and where each request stands, further down.",
            ),
        ),
        ChangelogEntry(
            version = "2026-08-28",
            date = "Aug 28, 2026",
            headline = "Search, categories, and the map",
            changes = listOf(
                "Journal has an always-visible filter bar: sort by distance from you, plus a chip for each category your places actually fall into.",
                "Search matches tags and neighbourhoods now, not just the place name — searching \"koreatown\" finds places tagged koreatown.",
                "Bar joins cafe, bakery and restaurant as a category.",
                "Directory: submitting a search ranks results by real distance and shows how far away each one is.",
                "Directory: \"Near me\" shows what is around you with no search at all, and can be narrowed to one category.",
                "Directory: places you import keep what kind of place they are, so they land in the right category.",
                "Directory asks for location permission instead of silently reporting that your location is unavailable.",
                "Map: +/- zoom buttons are back, and zooming in is no longer undone when you tap a pin.",
                "Map: pins in the same area group into one bubble, and tapping a pin names the place before opening it.",
                "The suggestion prompts at the top of Journal can be collapsed.",
                "New look throughout: the Warm Journal palette and typefaces.",
            ),
        ),
    )

    /** The newest entry, or null when nothing has shipped yet. */
    val latest: ChangelogEntry? get() = entries.firstOrNull()

    /**
     * Whether the reader still has something to catch up on.
     *
     * Compares against the newest version alone rather than tracking each entry: a reader
     * who opens the list has seen everything above the fold, and an alpha tester who skips
     * three builds wants "what's new" to mean the current build, not a backlog.
     */
    fun hasUnseen(lastSeenVersion: String?): Boolean {
        val newest = latest ?: return false
        return newest.version != lastSeenVersion
    }
}
